import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import MapPicker from '../components/MapPicker'
import PinGate from '../components/PinGate'
import { api, foutTekst } from '../lib/api'
import { matchesGewenstAdres, sortForAssignment } from '../lib/assignQueue'
import { shortDate } from '../lib/format'
import { spotDisplayNameById } from '../lib/spotLabel'
import type { AdminRegistration, MapMeta, PublicSpot } from '../lib/types'

export default function Toewijzen() {
  return (
    <PinGate>
      {(token, logout) => <Inner token={token} onLogout={logout} />}
    </PinGate>
  )
}

function sameSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const s = new Set(a)
  return b.every((x) => s.has(x))
}

function Inner({ token, onLogout }: { token: string; onLogout: () => void }) {
  const [registrations, setRegistrations] = useState<AdminRegistration[] | null>(null)
  const [baseSpots, setBaseSpots] = useState<PublicSpot[] | null>(null)
  const [meta, setMeta] = useState<MapMeta | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [working, setWorking] = useState<string[]>([])
  const [showAssigned, setShowAssigned] = useState(false)
  const [showWaitlist, setShowWaitlist] = useState(false)
  const [paidFilter, setPaidFilter] = useState<'alle' | 'betaald' | 'onbetaald'>('alle')
  const [paidBusyId, setPaidBusyId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current) }, [])

  async function load() {
    const [regsRes, spotsRes] = await Promise.all([
      api<{ registrations: AdminRegistration[] }>('/api/admin/registrations', {
        headers: { Authorization: `Bearer ${token}` },
      }),
      api<{ meta: MapMeta; spots: PublicSpot[] }>('/api/spots'),
    ])
    if (!regsRes.ok) {
      if (regsRes.status === 401) onLogout()
      setError('Inschrijvingen konden niet geladen worden.')
      return
    }
    if (!spotsRes.ok) { setError('Plekken konden niet geladen worden.'); return }
    setError(null)
    setRegistrations(regsRes.data.registrations)
    setBaseSpots(spotsRes.data.spots)
    setMeta(spotsRes.data.meta)
  }

  useEffect(() => { load() }, [token]) // eslint-disable-line react-hooks/exhaustive-deps

  const holderBySpot = useMemo(() => {
    const m = new Map<string, string>()
    for (const r of registrations ?? []) for (const id of r.spots) m.set(id, r.id)
    return m
  }, [registrations])

  const selected = registrations?.find((r) => r.id === selectedId) ?? null

  // Reset the working selection whenever a different registration is picked.
  useEffect(() => {
    setWorking(selected ? selected.spots : [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, registrations])

  const queue = useMemo(() => {
    let regs = registrations ?? []
    if (!showWaitlist) regs = regs.filter((r) => !r.wachtlijst)
    if (paidFilter === 'betaald') regs = regs.filter((r) => r.betaald)
    else if (paidFilter === 'onbetaald') regs = regs.filter((r) => !r.betaald)
    // Wie op betaling filtert wil iedereen zien (ook wie al een plek heeft) —
    // geld ophalen gaat over betaling, niet over toewijzing. Anders houden we
    // de gewone toewijs-wachtrij aan (enkel wie nog een plek mist).
    if (paidFilter === 'alle' && !showAssigned) regs = regs.filter((r) => r.spots.length < r.aantalPlaatsen)
    return sortForAssignment(regs)
  }, [registrations, showAssigned, showWaitlist, paidFilter])

  const progress = useMemo(() => {
    const regs = (registrations ?? []).filter((r) => !r.wachtlijst)
    const done = regs.filter((r) => r.aantalPlaatsen > 0 && r.spots.length >= r.aantalPlaatsen).length
    return { done, total: regs.length }
  }, [registrations])

  // Per-selection view of the polygons: a spot is "taken" only if someone
  // OTHER than the selected registration holds it — so the selected person's
  // own spots stay clickable (to release).
  const mapSpots = useMemo<PublicSpot[]>(() => {
    if (!baseSpots) return []
    if (!selected) return baseSpots
    return baseSpots.map((s) => ({
      ...s,
      taken: holderBySpot.has(s.id) && holderBySpot.get(s.id) !== selected.id,
    }))
  }, [baseSpots, selected, holderBySpot])

  // Highlight free spots whose street matches the registrant's wish.
  const recommendedIds = useMemo(() => {
    if (!selected || !baseSpots) return []
    return baseSpots
      .filter((s) => !working.includes(s.id))
      .filter((s) => !(holderBySpot.has(s.id) && holderBySpot.get(s.id) !== selected.id))
      .filter((s) => matchesGewenstAdres(s, selected.gewenstAdres))
      .map((s) => s.id)
  }, [selected, baseSpots, working, holderBySpot])

  function toggleSpot(id: string) {
    if (!selected) return
    setWorking((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id)
      if (prev.length >= selected.aantalPlaatsen) return prev
      return [...prev, id]
    })
  }

  // One click handler for the whole map. Clicking a spot held by SOMEONE ELSE
  // jumps to that person (inspect + act), so Simon doesn't have to hunt the
  // list. Clicking a free spot (or the selected person's own spot) assigns /
  // releases it for the selected person, as before.
  function onMapClick(id: string) {
    const holder = holderBySpot.get(id)
    if (holder && holder !== selectedId) {
      const isDirty = selected ? !sameSet(working, selected.spots) : false
      if (isDirty) { flash('Je hebt niet-opgeslagen wijzigingen — bevestig of annuleer eerst.'); return }
      setSelectedId(holder)
      return
    }
    if (!selected) { flash('Kies eerst links een inschrijving.'); return }
    toggleSpot(id)
  }

  // Clear all of the selected person's spots (make them free again).
  async function releaseAll() {
    if (!selected || selected.spots.length === 0) return
    if (!window.confirm(`Alle plek(ken) van ${selected.naam} vrijmaken?`)) return
    setBusy(true)
    const res = await api<{ freezeFailed: boolean }>(`/api/admin/registrations/${selected.id}/spot`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ spotIds: [] }),
    })
    setBusy(false)
    if (!res.ok) { alert('Vrijmaken mislukt.'); return }
    if (res.data.freezeFailed) {
      alert('Plek vrijgemaakt, maar het oude plek-adres kon niet gewist worden. Wijs opnieuw een plek toe en maak die daarna weer vrij om het te herstellen.')
    }
    flash(`Plek(ken) van ${selected.naam} vrijgemaakt.`)
    await load()
  }

  async function confirm() {
    if (!selected) return
    setBusy(true)
    const res = await api<{ freezeFailed: boolean }>(`/api/admin/registrations/${selected.id}/spot`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ spotIds: working }),
    })
    setBusy(false)
    if (!res.ok) {
      alert(res.error === 'taken'
        ? 'Een gekozen plek is net door iemand anders bezet. Herlaad en probeer opnieuw.'
        : foutTekst(res.error, 'Toewijzen mislukt.'))
      load()
      return
    }
    if (res.data.freezeFailed) {
      alert('Plek toegewezen, maar het plek-adres kon niet bevroren worden. Probeer de toewijzing opnieuw.')
    }
    flash(`Plek${working.length === 1 ? '' : 'ken'} toegewezen aan ${selected.naam}.`)
    // Stay on this person after saving so the "stuur plek-mail" button is
    // available; once "toon al toegewezen" is off they drop out of the queue
    // list and Simon picks the next one from the top.
    await load()
  }

  // Betaald aan/uit zetten — zelfde Betaald-checkbox in Notion als op /beheer.
  // stopPropagation zodat een tik op de badge de rij niet ook selecteert.
  async function togglePaid(r: AdminRegistration, e?: React.MouseEvent) {
    e?.stopPropagation()
    if (paidBusyId) return
    setPaidBusyId(r.id)
    const res = await api(`/api/admin/registrations/${r.id}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ betaald: !r.betaald }),
    })
    setPaidBusyId(null)
    if (!res.ok) { alert('Kon Betaald niet bijwerken.'); return }
    flash(!r.betaald ? `${r.naam} op betaald gezet.` : `${r.naam} terug op niet betaald.`)
    await load()
  }

  async function notify() {
    if (!selected) return
    setBusy(true)
    const res = await api<{ sent: boolean; reason?: string }>(
      `/api/admin/registrations/${selected.id}/notify-spot`,
      { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
    )
    setBusy(false)
    if (!res.ok) { alert('Mail versturen mislukt.'); return }
    if (res.data.sent) flash(`Plek-mail verstuurd naar ${selected.naam}.`)
    else flash(res.data.reason === 'no-email' ? `${selected.naam} heeft geen e-mailadres.` : 'Mail niet verstuurd.')
  }

  function flash(msg: string) {
    if (toastTimer.current) clearTimeout(toastTimer.current)
    setToast(msg)
    toastTimer.current = setTimeout(() => setToast(null), 4000)
  }

  if (error) {
    return (
      <div className="mx-auto max-w-5xl px-5 py-10">
        <p className="text-red font-semibold mb-3">{error}</p>
        <button onClick={onLogout} className="text-sm underline text-muted">Opnieuw aanmelden</button>
      </div>
    )
  }
  if (!registrations || !baseSpots || !meta) {
    return <div className="mx-auto max-w-5xl px-5 py-10 text-muted">Laden…</div>
  }

  const dirty = selected ? !sameSet(working, selected.spots) : false
  const savedCount = selected?.spots.length ?? 0
  const workingHasCarFree = working.some((id) => baseSpots?.find((s) => s.id === id)?.carFree)

  return (
    <div className="mx-auto max-w-7xl px-4 py-4">
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-baseline gap-3">
          <Link to="/beheer" className="text-sm underline text-muted">← Terug naar Beheer</Link>
          <h1 className="font-display text-3xl tracking-display uppercase">Plekken toewijzen</h1>
          <span className="text-sm text-muted">{progress.done} / {progress.total} toegewezen</span>
        </div>
        <button onClick={onLogout} className="text-sm text-muted underline">Afmelden</button>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Queue */}
        <aside className="lg:w-96 flex flex-col gap-2">
          <div className="flex flex-wrap gap-3 text-sm">
            <label className="flex items-center gap-1.5">
              <input type="checkbox" checked={showAssigned} onChange={(e) => setShowAssigned(e.target.checked)} />
              toon al toegewezen
            </label>
            <label className="flex items-center gap-1.5">
              <input type="checkbox" checked={showWaitlist} onChange={(e) => setShowWaitlist(e.target.checked)} />
              toon wachtlijst
            </label>
            <label className="flex items-center gap-1.5">
              <span>Betaling:</span>
              <select
                className="input w-auto px-2 py-1"
                value={paidFilter}
                onChange={(e) => setPaidFilter(e.target.value as 'alle' | 'betaald' | 'onbetaald')}
              >
                <option value="alle">alle</option>
                <option value="onbetaald">niet betaald</option>
                <option value="betaald">betaald</option>
              </select>
            </label>
          </div>
          <div className="text-xs text-muted">{queue.length} in de wachtrij · bewoners eerst, dan oudste inschrijving</div>
          <ul className="border-[3px] border-ink bg-offwhite max-h-[70vh] overflow-auto">
            {queue.map((r) => {
              const isSel = r.id === selectedId
              const full = r.aantalPlaatsen > 0 && r.spots.length >= r.aantalPlaatsen
              return (
                <li
                  key={r.id}
                  onClick={() => setSelectedId(r.id)}
                  className={`px-3 py-2 border-b border-line cursor-pointer text-sm ${isSel ? 'bg-lime' : 'hover:bg-lime/40'}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold">
                      {r.buurtbewoner && <span title="Buurtbewoner">🏠 </span>}
                      {r.wachtlijst && <span title="Wachtlijst">⏳ </span>}
                      {r.naam}
                    </span>
                    <span className={`text-xs font-display tracking-display ${full ? 'text-ink/50' : ''}`}>
                      {r.spots.length}/{r.aantalPlaatsen}
                    </span>
                  </div>
                  <div className="text-xs text-muted">
                    {shortDate(r.inschrijvingsdatum)} · {r.gewenstAdres}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
                    {r.spots.length > 0 && (
                      <span className="text-ink">📍 {r.spots.map((id) => spotDisplayNameById(id, baseSpots)).join(', ')}</span>
                    )}
                    <button
                      type="button"
                      onClick={(e) => togglePaid(r, e)}
                      disabled={paidBusyId === r.id}
                      title="Klik om betaald aan/uit te zetten"
                      className={`border-2 px-1.5 py-0.5 leading-none font-semibold disabled:opacity-50 ${
                        r.betaald ? 'border-ink bg-lime text-ink' : 'border-red bg-red/15 text-red'
                      }`}
                    >
                      {r.betaald ? '✓ betaald' : '✗ niet betaald'}
                    </button>
                    {!r.email && <span className="text-red font-semibold">⚠ geen e-mail</span>}
                  </div>
                  {r.notes && <div className="text-xs text-muted italic mt-0.5">"{r.notes}"</div>}
                </li>
              )
            })}
            {queue.length === 0 && (
              <li className="px-3 py-4 text-sm text-muted">Niks meer toe te wijzen 🎉</li>
            )}
          </ul>
        </aside>

        {/* Map + action panel */}
        <div className="flex-1 min-w-0">
          {selected ? (
            <div className="mb-3 border-[3px] border-ink bg-lime/30 p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="font-display text-2xl tracking-display uppercase">
                  {selected.buurtbewoner && '🏠 '}{selected.naam}
                </div>
                <div className="text-sm">
                  Wens: <strong>{selected.gewenstAdres}</strong> · {selected.aantalPlaatsen} plaats{selected.aantalPlaatsen > 1 ? 'en' : ''}
                </div>
              </div>
              {selected.notes && <div className="text-sm text-muted italic mt-1">"{selected.notes}"</div>}
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                {selected.email
                  ? <a href={`mailto:${selected.email}`} className="underline">{selected.email}</a>
                  : <span className="text-red font-semibold">⚠ geen e-mail</span>}
                <button
                  type="button"
                  onClick={() => togglePaid(selected)}
                  disabled={paidBusyId === selected.id}
                  title="Klik om betaald aan/uit te zetten"
                  className={`border-2 px-2 py-0.5 leading-none font-semibold disabled:opacity-50 ${
                    selected.betaald ? 'border-ink bg-lime text-ink' : 'border-red bg-red/15 text-red'
                  }`}
                >
                  {selected.betaald ? '✓ betaald' : '✗ niet betaald'}
                </button>
                {selected.spots.length > 0 && (
                  <span>
                    Huidige plek: <strong>{selected.spots.map((id) => spotDisplayNameById(id, baseSpots)).join(', ')}</strong>
                  </span>
                )}
              </div>
              {workingHasCarFree && (
                <div className="mt-2 text-sm border-[3px] border-ink bg-[#7fb3ff]/30 px-3 py-1.5">
                  ⚠️ Parkplek geselecteerd — hier kan je <strong>niet met de auto</strong> komen.
                </div>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <span className="text-sm">
                  Geselecteerd: <strong>{working.length} / {selected.aantalPlaatsen}</strong>
                  {working.length > 0 ? ` (${working.join(', ')})` : ''}
                </span>
                <button
                  onClick={confirm}
                  disabled={busy || !dirty}
                  className="px-4 py-2 bg-ink text-lime font-display tracking-display uppercase border-[3px] border-ink shadow-block-sm disabled:opacity-40 hover:translate-y-[1px]"
                >
                  {busy ? 'Bezig…' : dirty ? 'Bevestig plek(ken)' : 'Opgeslagen ✓'}
                </button>
                <button
                  onClick={notify}
                  disabled={busy || savedCount === 0 || dirty}
                  title={dirty ? 'Bevestig eerst de plek' : savedCount === 0 ? 'Nog geen plek toegewezen' : ''}
                  className="px-4 py-2 border-[3px] border-ink font-display tracking-display uppercase shadow-block-sm disabled:opacity-40 hover:translate-y-[1px]"
                >
                  ✉ Stuur plek-mail
                </button>
                <button
                  onClick={releaseAll}
                  disabled={busy || savedCount === 0}
                  title={savedCount === 0 ? 'Nog geen plek toegewezen' : 'Maakt alle plekken van deze persoon weer vrij'}
                  className="px-4 py-2 border-[3px] border-red text-red font-display tracking-display uppercase shadow-block-sm disabled:opacity-40 hover:translate-y-[1px]"
                >
                  🗑 Plek vrijmaken
                </button>
              </div>
            </div>
          ) : (
            <div className="mb-3 border-[3px] border-ink bg-offwhite p-3 text-sm text-muted">
              Kies links een inschrijving om plekken toe te wijzen — of <strong>klik op een bezette plek</strong> om te zien wie er staat. Oranje = plek in de gewenste straat.
            </div>
          )}

          <MapPicker
            spots={mapSpots}
            meta={meta}
            mode="multi"
            selectedIds={working}
            recommendedIds={recommendedIds}
            onToggle={onMapClick}
            allowTakenClicks
            height={560}
          />
          <Legend />
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-ink text-lime px-5 py-3 border-[3px] border-ink shadow-block z-50 font-display tracking-display uppercase text-sm">
          {toast}
        </div>
      )}
    </div>
  )
}

function Legend() {
  const swatch = (color: string) => (
    <span className="inline-block w-4 h-4 mr-1.5 align-middle border-[2px] border-ink" style={{ background: color }} />
  )
  return (
    <div className="flex flex-wrap gap-4 mt-3 text-sm font-display tracking-display uppercase">
      <span>{swatch('#e5ff73')}vrij</span>
      <span>{swatch('#7fb3ff')}park · geen auto</span>
      <span>{swatch('#5a5a5a')}bezet (ander)</span>
      <span>{swatch('#cf2e2e')}toegewezen aan deze persoon</span>
      <span>{swatch('#ff8800')}in gewenste straat</span>
    </div>
  )
}
